import React from 'react';
import type { CustomBriefAnswer, SiteRagReport } from '../../../../types/siteRag';
import { FindingClaimsList } from '../../report/FindingClaimsList';
import { SectionShell } from '../SectionShell';
import { walkCitationText } from '../../../../lib/walkCitationText';
import { parseCustomBriefQuestionSectionId } from '../../../../lib/customBriefSections';
import { useEffectiveReportTheme } from '../../../../lib/useEffectiveReportTheme';
import { roleColor } from '../../../../lib/reportRoleColors';
import { EditorialBadge, type EditorialBadgeTone } from '../../report/editorial';

interface Props {
  report: SiteRagReport;
  label: string;
  sectionId?: string;
}

const MONO = 'var(--report-body-font, inherit)';

/** Confidence → editorial tone (was filled green/amber/red pills). */
const CONFIDENCE_TONE: Record<string, EditorialBadgeTone> = {
  high: 'positive',
  medium: 'warning',
  low: 'negative',
};

function findAnswer(report: SiteRagReport, sectionId?: string): CustomBriefAnswer | undefined {
  const qid = sectionId ? parseCustomBriefQuestionSectionId(sectionId) : null;
  if (!qid) return undefined;
  return (report.customBriefAnswers ?? []).find(a => a.questionId === qid);
}

export const CustomBriefQuestionSection: React.FC<Props> = ({ report, label, sectionId }) => {
  const theme = useEffectiveReportTheme();
  const answer = findAnswer(report, sectionId);
  const hasData = Boolean(answer && (answer.findings.trim() || answer.evidence.trim()));

  return (
    <SectionShell label={label} hasData={hasData} report={report}>
      {!answer ? null : (
        <article className="space-y-4">
          <div>
            <h3
              className="text-xs font-semibold uppercase tracking-wide"
              style={{ fontFamily: MONO, color: roleColor(theme, 'muted') }}
            >
              Findings
            </h3>
            <p className="mt-1 text-sm leading-relaxed" style={{ color: roleColor(theme, 'body') }}>
              {walkCitationText(answer.findings, report.references)}
            </p>
          </div>
          <div>
            <h3
              className="text-xs font-semibold uppercase tracking-wide"
              style={{ fontFamily: MONO, color: roleColor(theme, 'muted') }}
            >
              Evidence
            </h3>
            <p className="mt-1 text-sm leading-relaxed" style={{ color: roleColor(theme, 'muted') }}>
              {walkCitationText(answer.evidence, report.references)}
            </p>
          </div>
          {answer.redFlags.length > 0 && (
            <FindingClaimsList
              entries={answer.redFlags}
              references={report.references}
              siteId={report.siteId}
              reportId={report.id}
              sectionId={sectionId}
              responses={report.openQuestionResponses}
              className="space-y-4"
            />
          )}
          <div>
            <h3
              className="text-xs font-semibold uppercase tracking-wide"
              style={{ fontFamily: MONO, color: roleColor(theme, 'muted') }}
            >
              Confidence
            </h3>
            <span className="mt-1 inline-flex">
              <EditorialBadge
                theme={theme}
                tone={CONFIDENCE_TONE[answer.confidence] ?? 'warning'}
                variant="outline"
              >
                {answer.confidence}
              </EditorialBadge>
            </span>
          </div>
        </article>
      )}
    </SectionShell>
  );
};
